import React, { useCallback, useMemo, useState } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'
import { getSecurityKey } from '../../../util/security'
import { Exchange, exchangeToJSON } from '../../../proto/generated/common'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../custom'
import useSetPrimarySecurity from '../../../hooks/useSetPrimarySecurity'
import { useSelector } from 'react-redux'
import { selectExchangesByCompanyCode } from '../../../selectors/security'
import { generatePath, useMatch, useNavigate } from 'react-router-dom'
import ROUTES from '../../../assets/routes'

interface ExchangeMenuButtonProps {
    currentExchange: Exchange
    companyCode: string | undefined
    symbol: string
    securities: any[]
    readOnly?: boolean
}

function exchangeButtonPropsAreEqual(prev: ExchangeMenuButtonProps, next: ExchangeMenuButtonProps) {
    const prevSec = prev.securities[0]
    const nextSec = next.securities[0]
    return (
        prev.currentExchange === next.currentExchange &&
        prev.companyCode === next.companyCode &&
        prev.symbol === next.symbol &&
        prev.readOnly === next.readOnly &&
        prevSec?.exchange === nextSec?.exchange &&
        prevSec?.code === nextSec?.code &&
        prevSec?.companyCode === nextSec?.companyCode
    )
}

const EXCHANGE_LABEL_SX = { padding: 0, margin: 0, maxHeight: '24px', fontSize: '14px' }

const ExchangeMenuButton: React.FC<ExchangeMenuButtonProps> = React.memo(({ currentExchange, companyCode, symbol, securities, readOnly }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const setPrimarySecurity = useSetPrimarySecurity()
    const fetchedExchanges = useSelector(selectExchangesByCompanyCode(securities[0]?.companyCode || ''))
    const isSymbolPage = useMatch(ROUTES.symbol)
    const navigate = useNavigate()
    const exchanges = useMemo(() => {
        return companyCode ? fetchedExchanges : [currentExchange]
    }, [companyCode, fetchedExchanges, currentExchange])

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (exchanges.length > 1) {
                setAnchorEl(event.currentTarget)
            }
        },
        [exchanges]
    )

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleExchangeSelect = useCallback(
        (exchange: Exchange) => {
            const key = getSecurityKey(exchange, symbol)
            setPrimarySecurity(key, securities, false)
            if (isSymbolPage) {
                navigate(generatePath(ROUTES.symbol, { symbol: key }), { replace: true })
            }
            handleClose()
        },
        [symbol, setPrimarySecurity, securities, isSymbolPage, navigate, handleClose]
    )

    const currentLabel = useMemo(() => exchangeToJSON(currentExchange).toUpperCase(), [currentExchange])

    if (readOnly) {
        return (
            <NonInteractiveTooltip title={'Exchange'} arrow placement="top">
                <Box component="span" sx={{ ...EXCHANGE_LABEL_SX, color: 'primary.main' }}>
                    {currentLabel}
                </Box>
            </NonInteractiveTooltip>
        )
    }

    if (exchanges.length <= 1) {
        return null
    }

    return (
        <>
            <NonInteractiveTooltip title={'Exchange'} arrow placement="top">
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    color="primary"
                    sx={EXCHANGE_LABEL_SX}
                    onClick={handleClick}
                >
                    {currentLabel}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{ dense: true }}>
                {exchanges.map((exchange, index) => (
                    <MenuItem key={index} onClick={() => handleExchangeSelect(exchange)} selected={exchange === currentExchange}>
                        {exchangeToJSON(exchange).toUpperCase()}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}, exchangeButtonPropsAreEqual)

export default ExchangeMenuButton
